import { Heart, Zap, TrendingUp, Users, Clock, Target, CheckCircle2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

const impacts = [
  {
    icon: Heart,
    title: "Stop Loneliness",
    stat: "89%",
    statLabel: "feel more connected",
    description: "Join a global community of focused individuals. Study alongside others, share your progress, and build meaningful connections through shared productivity goals.",
    benefits: [
      "Real-time study rooms with others",
      "Supportive community atmosphere",
      "Connect across time zones",
    ],
    gradient: "from-primary to-primary/60",
  },
  {
    icon: Zap,
    title: "Beat Procrastination",
    stat: "3x",
    statLabel: "more tasks completed",
    description: "Structured workflows and accountability features help you start tasks immediately. The presence of others working creates natural motivation to begin and stay focused.",
    benefits: [
      "Pomodoro timer for focused work",
      "Visual progress tracking",
      "Peer accountability system",
    ],
    gradient: "from-secondary to-secondary/60",
  },
  {
    icon: TrendingUp,
    title: "Boost Productivity",
    stat: "2.5x",
    statLabel: "increase in output",
    description: "Optimize your workflow with proven productivity techniques. Track your progress, manage tasks efficiently, and achieve more in less time with our integrated tools.",
    benefits: [
      "Smart task management",
      "Time tracking and analytics",
      "Distraction-free workspace",
    ],
    gradient: "from-primary via-secondary to-primary",
  },
];

export const Impact = () => {
  return (
    <section className="py-24 px-4 relative overflow-hidden" aria-labelledby="impact-title">
      {/* Animated background */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" aria-hidden="true" />
      <div className="absolute top-20 left-10 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse" aria-hidden="true" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-secondary/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} aria-hidden="true" />
      
      <div className="container mx-auto relative z-10">
        {/* Header */}
        <header className="text-center mb-16 animate-fade-in">
          <div className="inline-block mb-4 px-6 py-2 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-full border border-primary/30">
            <span className="text-sm font-semibold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              PROVEN RESULTS
            </span>
          </div>
          <h2 id="impact-title" className="font-display text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent">
            Transform Your Work Life
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Backed by research and proven by thousands of users worldwide, CSW delivers measurable improvements in focus, connection, and productivity.
          </p>
        </header>

        {/* Impact Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto mb-20" role="list">
          {impacts.map((impact, index) => {
            const Icon = impact.icon;
            return (
              <article
                key={impact.title}
                className="group relative overflow-hidden border-2 hover:border-primary/50 transition-all duration-500 hover:scale-105 hover:shadow-2xl animate-fade-in bg-card/50 backdrop-blur-sm rounded-lg"
                style={{ animationDelay: `${index * 0.15}s` }}
                role="listitem"
              >
                {/* Gradient background */}
                <div className={`absolute inset-0 bg-gradient-to-br ${impact.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} aria-hidden="true" />
                
                <div className="p-8">
                  {/* Icon */}
                  <div className="mb-6" aria-hidden="true">
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${impact.gradient} flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow duration-300`}>
                      <Icon className="w-8 h-8 text-primary-foreground" />
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold mb-4 text-foreground">
                    {impact.title}
                  </h3>

                  {/* Stat */}
                  <aside className="mb-6" aria-label={`${impact.title} statistics`}>
                    <div className={`text-5xl font-bold bg-gradient-to-r ${impact.gradient} bg-clip-text text-transparent mb-2`}>
                      {impact.stat}
                    </div>
                    <div className="text-sm text-muted-foreground font-medium uppercase tracking-wide">
                      {impact.statLabel}
                    </div>
                  </aside>

                  {/* Description */}
                  <p className="text-muted-foreground mb-6 leading-relaxed">
                    {impact.description}
                  </p>

                  {/* Benefits list */}
                  <ul className="space-y-3" aria-label={`${impact.title} benefits`}>
                    {impact.benefits.map((benefit) => (
                      <li key={benefit} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" aria-hidden="true" />
                        <span className="text-sm text-foreground">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            );
          })}
        </div>

        {/* Bottom CTA section */}
        <aside className="text-center animate-fade-in" style={{ animationDelay: "0.6s" }} aria-label="Community statistics">
          <div className="max-w-4xl mx-auto border-2 border-primary/20 bg-gradient-to-br from-card to-accent/20 backdrop-blur-sm rounded-lg">
            <div className="p-12">
              <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                <div className="flex-1 text-left">
                  <h3 className="font-display text-3xl font-bold mb-3 text-foreground">
                    Join Thousands Who Have Transformed Their Productivity
                  </h3>
                  <p className="text-muted-foreground text-lg">
                    Start your journey to better focus, stronger connections, and higher achievement today.
                  </p>
                </div>
                <div className="flex flex-col gap-4 items-center">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-primary" aria-hidden="true" />
                    <span className="text-sm font-semibold text-foreground">50,000+ Active Users</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-secondary" aria-hidden="true" />
                    <span className="text-sm font-semibold text-foreground">2M+ Hours Logged</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-primary" aria-hidden="true" />
                    <span className="text-sm font-semibold text-foreground">95% Satisfaction Rate</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
};
